import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, LogOut, Users, Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Sidebar, { useIsMobile } from "@/components/Sidebar";
import UpsellDialog from "@/components/UpsellDialog";
import OutstandingPurchasesBadge from "@/components/OutstandingPurchasesBadge";
import NotificationBell from "@/components/NotificationBell";
import OnboardingChecklist from "@/components/onboarding/OnboardingChecklist";
import { planLabel } from "@/lib/plans";

export default function AppShell() {
  const { t } = useTranslation();
  const { session, logout, switchTenant, pendingActionsCount } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [upsell, setUpsell] = useState(null);

  if (!session) return null;

  const onSwitch = async (id) => {
    if (id === session.tenant.id) return;
    await switchTenant(id);
    navigate("/app");
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className={isMobile ? "pt-14" : "ml-[220px]"}>
        <header className="h-16 sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200 px-6 flex items-center justify-between"
                data-testid="app-header">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost"
                      data-testid="tenant-switcher"
                      className="gap-2 text-slate-900 hover:bg-slate-100">
                <div className="text-left">
                  <div className="text-sm font-semibold" style={{fontFamily: "Manrope, sans-serif"}}>
                    {session.tenant.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    {planLabel(session.tenant.plan_code)}
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">
                {t("tenantSwitcher.placeholder", "Mes entités")}
              </DropdownMenuLabel>
              {(session.memberships || []).map((m) => (
                <DropdownMenuItem
                  key={m.id}
                  onClick={() => m.id !== session.tenant.id && onSwitch(m.id)}
                  data-testid={`switch-tenant-${m.id}`}
                  className="gap-2 cursor-pointer">
                  <div className="flex-1">
                    <div className="text-sm">{m.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                      {planLabel(m.plan_code)} · {t(`roles.${m.role}`, m.role)}
                    </div>
                  </div>
                  {m.id === session.tenant.id && <Check className="h-4 w-4 text-blue-600" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate("/app/entities")}
                data-testid="manage-entities"
                className="text-sm text-slate-700 cursor-pointer">
                <Users className="h-4 w-4 mr-2 text-slate-500" strokeWidth={2} />
                Gérer mes entités
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-3">
            <OutstandingPurchasesBadge />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="user-menu-trigger"
                        className="gap-2 text-slate-700">
                  <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-medium text-slate-700">
                    {(session.user.full_name || session.user.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="text-sm font-medium text-slate-900">
                      {session.user.full_name || session.user.email}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                      {session.role_label}
                    </div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500">
                  {session.user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/login"); }}
                  data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> {t("common.signOut", "Se déconnecter")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-6 sm:p-8 max-w-[1400px] mx-auto w-full">
          <Outlet />
        </main>
      </div>

      <UpsellDialog
        open={!!upsell}
        onOpenChange={(v) => !v && setUpsell(null)}
        feature={upsell?.feature}
        included={upsell?.included}
        onGoToPlan={() => { setUpsell(null); navigate("/app/plan"); }}
      />

      {/* V4 — Widget d'activation nouveaux tenants (auto-hide) */}
      <OnboardingChecklist />
    </div>
  );
}
